import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { ConfirmActionDialog, PageActionRow, PageDivider, PageFrame, PrimaryActionButton } from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import { useLinkedEntityCards } from '../../../shared/components/linked-entities/LinkedEntityCardHost';
import {
  ProposalsHeader,
  ProposalStats,
  ProposalFilters,
  ProposalList,
  ProposalDetailDialog,
  CreateProposalDialog,
} from '@/modules/deals/components/proposals';
import { useProposalsData } from '@/modules/deals/hooks';
import '../styles/deals.css';

const DealProposalsPage = () => {
  const { capabilities } = useSession();
  const { openEntityCard } = useLinkedEntityCards();
  const {
    proposalStages,
    filteredProposals,
    stats,
    filters,
    activeOverviewFilter,
    activeClientFilterLabel,
    proposalsModuleDisabled,
    proposalsError,
    proposalsTenantDatabaseBlocked,
    setSearchTerm,
    setPriorityFilter,
    setAssigneeFilter,
    setStageFilter,
    setClientFilter,
    clearFilters,
    setOverviewFilter,
    isDetailDialogOpen,
    selectedProposal,
    openProposalDetails,
    closeProposalDetails,
    handleSaveProposal,
    handleDeleteProposal,
    handlePromoteProposal,
    promotionStates,
    isCreateDialogOpen,
    openCreateDialog,
    closeCreateDialog,
    handleCreateProposal,
    onDragEnd,
    staff,
    staffUnavailable,
    staffCatalogStatus,
    clients,
    assigneeOptions,
    hasActiveFilters,
  } = useProposalsData();
  const moduleFilters = useRegisteredModuleFilters('proposals', filteredProposals);
  const [deleteRequest, setDeleteRequest] = useState(null);

  function openClientDetail(clientId) {
    openEntityCard({ entityType: 'client', entityId: clientId });
  }

  function openResponsibleStaff(staffId, staffMember) {
    const resolvedId = staffId || staffMember?.id;
    if (resolvedId) openEntityCard({ entityType: 'staff', entityId: resolvedId });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="deals-page">
      {!proposalsModuleDisabled ? <ProposalsHeader onCreateProposal={openCreateDialog} /> : null}

      {proposalsModuleDisabled ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {proposalsError || 'Modulo affari non abilitato per questo workspace.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!proposalsModuleDisabled && proposalsTenantDatabaseBlocked ? (
        <Alert className="deals-alert">
          <AlertDescription>
            {proposalsError || 'Database tenant non disponibile per il modulo affari.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked ? (
        <ProposalStats
          stats={stats}
          activeMetric={activeOverviewFilter}
          onMetricClick={setOverviewFilter}
        />
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked && filters.clientFilter !== 'all' ? (
        <Alert variant="info" className="deals-alert deals-alert--client">
          <AlertDescription>
            Vista filtrata per cliente: {activeClientFilterLabel}. Usa Reset per rimuovere il filtro.
          </AlertDescription>
        </Alert>
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked && staffUnavailable ? (
        <Alert className="deals-alert">
          <AlertDescription>
            Catalogo staff non disponibile dal backend. Le selezioni personale sono momentaneamente vuote.
          </AlertDescription>
        </Alert>
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked ? (
        <>
        <PageDivider />
        <ProposalFilters
          filters={filters}
          onSearchChange={setSearchTerm}
          onPriorityChange={setPriorityFilter}
          onAssigneeChange={setAssigneeFilter}
          onStageChange={setStageFilter}
          onClientChange={setClientFilter}
          onClearFilters={clearFilters}
          assigneeOptions={assigneeOptions}
          clientOptions={clients.map((client) => ({
            value: String(client.id),
            label: client.company || client.businessName || client.name,
          }))}
          stageOptions={proposalStages}
          hasActiveFilters={hasActiveFilters}
        />
        <PageDivider />
        <PageActionRow>
          <PrimaryActionButton onClick={openCreateDialog}>Nuovo</PrimaryActionButton>
        </PageActionRow>
        </>
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked ? (
        <ProposalList
          proposalStages={proposalStages}
          proposals={moduleFilters.filterRows(filteredProposals)}
          onCardClick={openProposalDetails}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          onDragEnd={onDragEnd}
          staff={staff}
        />
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked ? (
        <ProposalDetailDialog
          proposal={selectedProposal}
          isOpen={isDetailDialogOpen}
          onClose={closeProposalDetails}
          onSave={handleSaveProposal}
          onPromote={handlePromoteProposal}
          promotionState={selectedProposal ? promotionStates[String(selectedProposal.id)] || {} : {}}
          onDelete={async (proposalId) => {
            setDeleteRequest({ id: proposalId });
          }}
          onOpenClient={openClientDetail}
          onOpenStaff={openResponsibleStaff}
          staff={staff}
          clients={clients}
          canManageDealDestructiveActions={capabilities?.canManageDealDestructiveActions === true}
        />
      ) : null}

      {!proposalsModuleDisabled && !proposalsTenantDatabaseBlocked ? (
        <CreateProposalDialog
          isOpen={isCreateDialogOpen}
          onClose={closeCreateDialog}
          onCreate={handleCreateProposal}
          stages={proposalStages}
          staff={staff}
          staffCatalogStatus={staffCatalogStatus}
          clients={clients}
        />
      ) : null}

      <ConfirmActionDialog
        open={Boolean(deleteRequest)}
        title="Eliminare la proposta?"
        description="La proposta verrà rimossa definitivamente."
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setDeleteRequest(null)}
        onConfirm={async () => {
          const result = await handleDeleteProposal(deleteRequest.id);
          setDeleteRequest(null);
          toast({
            title: result.ok ? 'Proposta eliminata' : 'Eliminazione non riuscita',
            description: result.message,
            variant: result.ok ? 'default' : 'destructive',
          });
        }}
      />
    </PageFrame>
  );
};

export default DealProposalsPage;
